import type { RegimeProfileThresholds, RegimeProfileVetoCandidate, RegimeProfileVerdictResult } from '@/types/appRouter';
import { REGIME_MARKET_COMPOSITE_FEATURE_KEY, type TradeLogicSeedVetoCondition } from '@/features/tradeLogicAuthoring/seedCondition';
import { formatShare, formatSignedPercent } from './regimeProfilePresentation';

/**
 * Pure presentation logic for Layer 3 — the veto candidate card
 * (docs/plans/REGIME_DEPENDENCE_PROFILE_FRONTEND_PLAN.md, "Layer 3" and "The action path").
 *
 * Veto candidates are exclusion-shaped research treatments supplied by the backend report.
 * This module renders the saved candidates without adding economic eligibility requirements. Thresholds come from the report's echoed `thresholds` block, never from constants here.
 */

/**
 * Authoring feature behind each veto dimension. `regime.marketRegime.compositeState` is the canonical
 * contextFeature for `market.compositeState` (enum feature, operators `=` / `!=` only — which is why a
 * multi-state veto seeds one `!=` condition per state rather than a single `not_in`).
 */
const VETO_DIMENSION_FEATURE_KEYS: Record<RegimeProfileVetoCandidate['dimensionKey'], string> = {
    'market.compositeState': REGIME_MARKET_COMPOSITE_FEATURE_KEY
};

const STRATEGY_VARIANT_LABEL_MAX_LENGTH = 125;

export const buildRegimeVetoSeedCondition = (
    candidate: Pick<RegimeProfileVetoCandidate, 'dimensionKey' | 'excludedStates'>
): TradeLogicSeedVetoCondition => {
    const featureKey: string | undefined = VETO_DIMENSION_FEATURE_KEYS[candidate.dimensionKey];

    if (featureKey === undefined) {
        throw new Error(`No authoring feature is mapped for veto dimension ${candidate.dimensionKey}.`);
    }

    if (candidate.excludedStates.length === 0) {
        throw new Error('Veto candidate has no excluded states.');
    }

    return {
        excludedStates: [...candidate.excludedStates],
        featureKey,
        kind: 'contextFeature',
        operator: '!='
    };
};

/** e.g. `regime-veto-bearStressed`, multi-state `regime-veto-bearStressed-rangeRotational`. */
export const deriveRegimeVetoVariantLabel = (excludedStates: readonly string[]): string => {
    if (excludedStates.length === 0) {
        throw new Error('Veto candidate has no excluded states.');
    }

    return `regime-veto-${excludedStates.join('-')}`.slice(0, STRATEGY_VARIANT_LABEL_MAX_LENGTH);
};

/** Variant display name, capped to the backend's 125-char name limit (source name trimmed first). */
export const deriveRegimeVetoVariantName = (sourceStrategyName: string, excludedStates: readonly string[]): string => {
    const suffix = ` — regime veto (${excludedStates.join(', ')})`;

    if (suffix.length >= STRATEGY_VARIANT_LABEL_MAX_LENGTH) {
        return deriveRegimeVetoVariantLabel(excludedStates);
    }

    const maxSourceLength = STRATEGY_VARIANT_LABEL_MAX_LENGTH - suffix.length;
    const trimmedSource = sourceStrategyName.length > maxSourceLength ? sourceStrategyName.slice(0, maxSourceLength) : sourceStrategyName;

    return `${trimmedSource}${suffix}`;
};

export interface VetoRetentionBarLayout {
    /** Retained trade share as a 0–100 track percentage. */
    retainedPercent: number;
    /** Position of the `vetoMinRetainedTradeShare` marker as a 0–100 track percentage. */
    minMarkerPercent: number;
    meetsMinimum: boolean;
}

const clampShare = (share: number): number => Math.min(1, Math.max(0, share));

export const buildVetoRetentionBarLayout = (retainedTradeShare: number, vetoMinRetainedTradeShare: number): VetoRetentionBarLayout => ({
    meetsMinimum: retainedTradeShare >= vetoMinRetainedTradeShare,
    minMarkerPercent: clampShare(vetoMinRetainedTradeShare) * 100,
    retainedPercent: clampShare(retainedTradeShare) * 100
});

/** `market.compositeState ∈ { bearStressed, rangeRotational }` — the states whose membership is vetoed. */
export const formatVetoConditionDisplay = (candidate: Pick<RegimeProfileVetoCandidate, 'dimensionKey' | 'excludedStates'>): string =>
    `${candidate.dimensionKey} ∈ { ${candidate.excludedStates.join(', ')} }`;

export interface RegimeVetoCandidateCardModel {
    /** Stable per candidate within one report; safe as a React key and pending-action marker. */
    key: string;
    candidate: RegimeProfileVetoCandidate;
    conditionDisplay: string;
    retention: VetoRetentionBarLayout;
    retainedShareDisplay: string;
    minRetainedShareDisplay: string;
    excludedShareDisplay: string;
    baselineMeanDisplay: string;
    retainedMeanDisplay: string;
    variantLabel: string;
    seedCondition: TradeLogicSeedVetoCondition;
}

/**
 * One card model per veto candidate — the contract allows several, each independently actionable. An
 * empty result means Layer 3 does not render at all.
 */
export const buildVetoCandidateCardModels = (
    verdict: Pick<RegimeProfileVerdictResult, 'vetoCandidates'>,
    thresholds: Pick<RegimeProfileThresholds, 'vetoMinRetainedTradeShare'>
): RegimeVetoCandidateCardModel[] =>
    verdict.vetoCandidates.map((candidate) => ({
        baselineMeanDisplay: formatSignedPercent(candidate.baselineMeanProfitPercent),
        candidate,
        conditionDisplay: formatVetoConditionDisplay(candidate),
        excludedShareDisplay: formatShare(candidate.excludedTradeShare, 1),
        key: `${candidate.dimensionKey}:${candidate.excludedStates.join('|')}`,
        minRetainedShareDisplay: formatShare(thresholds.vetoMinRetainedTradeShare),
        retainedMeanDisplay: formatSignedPercent(candidate.retainedMeanProfitPercent),
        retainedShareDisplay: formatShare(candidate.retainedTradeShare, 1),
        retention: buildVetoRetentionBarLayout(candidate.retainedTradeShare, thresholds.vetoMinRetainedTradeShare),
        seedCondition: buildRegimeVetoSeedCondition(candidate),
        variantLabel: deriveRegimeVetoVariantLabel(candidate.excludedStates)
    }));
